// ============================================================
// TMR Portal Admin — como cada evento da linha do tempo se lê (07 §5)
// ============================================================
import type { ActivityCategory, ActivityEntry } from './contract';
import { ruleOptionCopy, scoreBandsLabel } from '../businessRules/status';
import { segmentLabel } from '../companies/segment';
import type { RuleValue } from '../businessRules/contract';

/**
 * The tab labels. PT-BR, like every product copy — the keys they map from are English and stable.
 */
export const CATEGORY_LABEL: Record<ActivityCategory, string> = {
  Registration: 'Cadastro',
  Contract: 'Contratos',
  Finance: 'Financeiro',
  Rates: 'Taxas',
  BusinessRule: 'Regras de negócio',
  PatientContract: 'Contrato de Paciente',
  Credit: 'Crédito',
  Contingency: 'Contingência',
  System: 'Sistema',
};

/**
 * The order the tabs read in, which is roughly the order an Empresa lives through them: it is registered,
 * it signs a contract, its money starts moving, its price and the rules it sells by get negotiated, its
 * patients start signing — and the platform's own doings come last because they are the ones nobody is
 * looking for. "Regras de negócio" sits right after "Taxas", as the tab does on the Empresa's screen.
 */
export const CATEGORY_ORDER: ActivityCategory[] = [
  'Registration',
  'Contract',
  'Finance',
  'Rates',
  'BusinessRule',
  'PatientContract',
  'Credit',
  'Contingency',
  'System',
];

/**
 * How each `type` reads on screen.
 *
 * **This map is the frontend half of the feature, and it is deliberately here rather than in the backend.**
 * The backend stores a key plus structured references and never a sentence (`07` §5), so rewording anything
 * an operator reads — or translating it — never touches a stored row, and a type that is retired stops
 * being emitted without rewriting history.
 *
 * Each entry is a function of the payload, so a phrase can name the thing it happened to. Keep them short:
 * the actor and the timestamp are rendered beside them, so repeating "por Fulano" here would say it twice.
 */
const PHRASES: Record<string, (payload: Payload) => string> = {
  // --- Cadastro ---
  'company.registered': () => 'Empresa cadastrada',
  // Registrado junto do `company.registered` quando o cadastro nasce de uma solicitação do site (28 §10).
  'company.application_approved': (p) =>
    p.protocol
      ? `Cadastro aprovado a partir da solicitação ${p.protocol} do site`
      : 'Cadastro aprovado a partir de uma solicitação do site',
  'company.imported': (p) =>
    p.legacyProducerId ? `Empresa importada do legado (produtor ${p.legacyProducerId})` : 'Empresa importada do legado',
  'company.updated': () => 'Cadastro alterado',
  'company.status_changed': (p) =>
    p.from && p.to
      ? `Situação alterada de ${statusLabel(p.from)} para ${statusLabel(p.to)}`
      : 'Situação alterada',
  'company.representative_changed': () => 'Representante legal substituído',
  // O nome vem no payload quando alguém foi nomeado; sem ele, o gestor foi removido.
  'company.sales_manager_changed': (p) =>
    p.name ? `Gestor de venda definido: ${p.name}` : 'Gestor de venda removido',
  'company.post_sales_manager_changed': (p) =>
    p.name ? `Gestor de pós-venda definido: ${p.name}` : 'Gestor de pós-venda removido',
  'company.segment_filled': (p) =>
    p.segment
      ? `Segmento preenchido pelos produtos do legado: ${segmentLabel(p.segment)}`
      : 'Segmento preenchido pelos produtos do legado',

  // --- Sistema ---
  'company.exported': (p) =>
    p.legacyProducerId
      ? `Replicada para o sistema legado (produtor ${p.legacyProducerId})`
      : 'Replicada para o sistema legado',
  'company.export_retried': () => 'Replicação reprocessada',

  // --- Contratos ---
  'contract.created': () => 'Contrato criado',
  'contract.sent': (p) =>
    p.target ? `Contrato enviado para assinatura (${p.target})` : 'Contrato enviado para assinatura',
  'contract.resent': (p) =>
    p.target
      ? `Solicitação de assinatura reenviada (${p.target})`
      : 'Solicitação de assinatura reenviada',
  // Both contacts when both are known: "went to the wrong number, then to this one" is the whole story.
  'contract.signatory_changed': (p) =>
    p.previousTarget && p.target
      ? `Contrato enviado para outro contato: ${p.previousTarget} → ${p.target}`
      : p.target
        ? `Contrato enviado para outro contato (${p.target})`
        : 'Contrato enviado para outro contato',
  'contract.signed': () => 'Contrato assinado',
  'contract.refused': () => 'Assinatura recusada',
  'contract.expired': () => 'Prazo de assinatura expirado',
  'contract.cancelled': (p) =>
    p.cancellationReason ? `Contrato cancelado — ${p.cancellationReason}` : 'Contrato cancelado',

  // --- Financeiro ---
  'account.opened': () => 'Conta corrente aberta',
  'account.withdrawal_allowed': () => 'Conta corrente liberada para saque',
  'receiving_account.added': (p) =>
    p.bankName ? `Conta de recebimento cadastrada (${p.bankName})` : 'Conta de recebimento cadastrada',
  'receiving_account.updated': (p) =>
    p.bankName ? `Conta de recebimento alterada (${p.bankName})` : 'Conta de recebimento alterada',
  'receiving_account.default_changed': (p) =>
    p.bankName ? `Conta de recebimento padrão alterada para ${p.bankName}` : 'Conta de recebimento padrão alterada',

  // --- Taxas ---
  'rate.created': () => 'Taxa criada',
  'rate.updated': (p) => withTransition('Taxa alterada', asNumber(p.previousValue), asNumber(p.newValue)),
  'rate.deleted': () => 'Taxa excluída',
  'rate.temporary_applied': (p) =>
    p.reason ? `Taxa temporária aplicada — ${p.reason}` : 'Taxa temporária aplicada',
  'rate.temporary_ended': () => 'Taxa temporária encerrada',

  // --- Regras de negócio ---
  // A primeira chave tem sujeito `Global`: aparece na linha do tempo de TODA empresa, então a frase diz que
  // é o padrão da TMR — lida dentro de uma clínica, não pode parecer uma exceção daquela clínica.
  'business_rule.default_changed': (p) =>
    withValues(`Padrão da TMR alterado: ${p.ruleName ?? 'regra de negócio'}`, p, p.previousValue, p.newValue),
  'business_rule.override_set': (p) =>
    p.previousValue == null
      ? withValue(`Exceção definida: ${p.ruleName ?? 'regra de negócio'}`, p, p.newValue)
      : withValues(`Exceção alterada: ${p.ruleName ?? 'regra de negócio'}`, p, p.previousValue, p.newValue),
  'business_rule.override_removed': (p) =>
    withValue(
      `Exceção removida: ${p.ruleName ?? 'regra de negócio'} — volta a valer o padrão da TMR`,
      p,
      p.defaultValue,
    ),

  // --- Crédito ---
  // Nunca o CPF, o nome nem o score: o payload traz só ids e o resultado (`26` §10).
  'credit.manual_review_decided': (p) =>
    p.outcome === 'approved'
      ? 'Venda aprovada pela TMR na análise de crédito'
      : p.outcome === 'rejected'
        ? 'Venda reprovada pela TMR na análise de crédito'
        : 'Análise de crédito decidida pela TMR',
  'credit.bureau_report_requested': () => 'Nova consulta ao Serasa pedida',

  // --- Contingência ---
  // Uma entrada por clínica cuja venda o código abre. Nunca o CPF, o nome nem o código (`30` §10).
  'contingency.wildcard_code_issued': () => 'Código coringa gerado para um paciente desta clínica entrar no checkout',

  // --- Contrato de Paciente ---
  // As duas primeiras chaves têm sujeito `Global`: são gravadas uma vez e aparecem na linha do tempo de
  // TODA empresa (`07` §5). Por isso a frase diz "global" — lida dentro de uma empresa, ela precisa deixar
  // claro que não é uma exceção daquela clínica.
  'patient_contract_template.created': (p) =>
    p.documentTypeName
      ? `Modelo global de ${p.documentTypeName} criado`
      : 'Modelo global de contrato criado',
  'patient_contract_template.published': (p) =>
    withVersionNote(
      p.documentTypeName
        ? `Modelo global de ${p.documentTypeName} publicado`
        : 'Modelo global de contrato publicado',
      p,
    ),
  'patient_contract_template.override_created': (p) =>
    p.documentTypeName
      ? `Modelo próprio de ${p.documentTypeName} criado para esta empresa`
      : 'Modelo próprio de contrato criado para esta empresa',
  'patient_contract_template.override_published': (p) =>
    withVersionNote(
      p.documentTypeName
        ? `Modelo próprio de ${p.documentTypeName} publicado`
        : 'Modelo próprio de contrato publicado',
      p,
    ),
  'patient_contract_template.override_archived': (p) =>
    p.documentTypeName
      ? `Modelo próprio de ${p.documentTypeName} arquivado — volta a valer o modelo global`
      : 'Modelo próprio de contrato arquivado — volta a valer o modelo global',
  // O evento registra o pedido, não o resultado: o desfecho chega como evento do próprio contrato, na
  // cadeia que o legado grava. Por isso a frase fala em "reprocessamento pedido".
  'patient_contract.reprocessing_requested': (p) =>
    p.patientName
      ? `Reprocessamento do contrato de ${p.patientName} pedido`
      : 'Reprocessamento de contrato de paciente pedido',
};

/**
 * The sentence for an entry, in PT-BR.
 *
 * **An unknown type is rendered, not dropped.** A deploy can put a `type` in the database that this build
 * has never heard of — that is the normal consequence of the backend and the portal shipping separately —
 * and a screen that threw, or silently hid the line, would be worse than one that says something plain. The
 * fallback is deliberately readable rather than technical, because an operator will see it.
 *
 * @param entry The timeline entry.
 */
export function activityPhrase(entry: ActivityEntry): string {
  const phrase = PHRASES[entry.type];
  if (phrase) return phrase(payloadOf(entry));

  return `Evento registrado (${entry.type})`;
}

/**
 * Who is credited with the entry.
 *
 * `System` has no name on purpose — how the platform reads on screen is product copy, so the backend stores
 * nothing for it and the word lives here. A `User` or `Party` with no name is a real gap in the record
 * rather than an automatic action, and says so instead of silently reading as "Sistema".
 *
 * @param entry The timeline entry.
 */
export function activityActor(entry: ActivityEntry): string {
  if (entry.actorKind === 'System') return 'Sistema';
  return entry.actorName ?? 'Autor não registrado';
}

/**
 * The instant an entry happened, in the operator's timezone: `21/08/2026 às 14:30`.
 *
 * @param iso The ISO timestamp from the API.
 */
export function activityInstant(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
    .format(date)
    .replace(', ', ' às ');
}

/** The payload as the phrase map reads it — every field optional, because every type carries its own. */
interface Payload {
  /** The segment the legacy backfill filled in, as its catalogue key (`32` §12). */
  segment?: string;
  /** The credit decision: `approved` or `rejected` (`26` §10). */
  outcome?: string;
  /** The site application an Empresa was approved from (`28` §10). */
  protocol?: string;
  name?: string;
  cnpj?: string;
  subdomain?: string;
  from?: string;
  to?: string;
  legacyProducerId?: number;
  contractId?: string;
  target?: string;
  /** The contact a document went to before its signatory was replaced; null for an older contract. */
  previousTarget?: string | null;
  cancellationReason?: string;
  bankName?: string;
  reason?: string;
  /** A number on the rate keys; a number or an option's string on the business rule keys. */
  previousValue?: RuleValue | null;
  newValue?: RuleValue | null;
  documentTypeName?: string;
  versionNumber?: number;
  note?: string;
  patientName?: string;
  ruleKey?: string;
  ruleName?: string;
  /**
   * A business rule's values arrive raw (`25`): a number for the down payment rule, the option's string for
   * the sales module (`"novo"`). `previousValue` and `newValue` are shared with the rate keys above; on
   * `business_rule.override_set` the previous one is null when the Empresa had no exception before.
   */
  defaultValue?: RuleValue | null;
  removedValue?: RuleValue | null;
}

/** Narrows the untyped payload once, at the only place with a reason to know its shape. */
function payloadOf(entry: ActivityEntry): Payload {
  return (entry.payload ?? {}) as Payload;
}

/** The Empresa's lifecycle states, in the operator's words — the same vocabulary the Empresa header uses. */
function statusLabel(status: string): string {
  const labels: Record<string, string> = {
    PendingContract: 'Aguardando contrato',
    AwaitingSignature: 'Aguardando assinatura',
    Completed: 'Ativa',
    Active: 'Ativa',
    Inactive: 'Inativa',
  };

  return labels[status] ?? status;
}

/**
 * Appends `de X para Y` when both halves of a change were recorded.
 *
 * Values are shown raw rather than formatted as a rate, and that is a limitation worth being honest about:
 * the entry does not carry the rate's type, so the unit (percent, reais, days) is unknown here. The
 * detailed before-and-after lives in the rate's own history (`15` §3 F), which this line points at.
 */
function withTransition(prefix: string, previous?: number, next?: number): string {
  if (previous === undefined || next === undefined) return prefix;
  return `${prefix} de ${formatNumber(previous)} para ${formatNumber(next)}`;
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    .format(value);
}

/**
 * A business rule's value appended to a phrase — `(4)`, `(Vendas)` — when the entry carries it.
 *
 * A number is shown raw: its reading ("entrada + 3x") belongs to the rule's own screen. An option is shown by
 * its name, because the stored word is not what the operator chose — "novo" was picked as "Vendas".
 */
function withValue(prefix: string, payload: Payload, value?: RuleValue | null): string {
  return value == null ? prefix : `${prefix} (${ruleValueText(payload, value)})`;
}

/** Both ends of a business rule's change, when both were recorded: `de 3 para 4`, `de Legado para Vendas`. */
function withValues(prefix: string, payload: Payload, previous?: RuleValue | null, next?: RuleValue | null): string {
  if (previous == null || next == null) return withValue(prefix, payload, next);
  return `${prefix}, de ${ruleValueText(payload, previous)} para ${ruleValueText(payload, next)}`;
}

/** One business rule value as the phrase reads it — see {@link withValue}. */
function ruleValueText(payload: Payload, value: RuleValue): string {
  const rule = { key: payload.ruleKey ?? '' };
  if (Array.isArray(value)) return scoreBandsLabel(rule, value);
  return typeof value === 'string' ? ruleOptionCopy(rule, value).label : String(value);
}

/** A rate's value, which is always a number — anything else is left out rather than printed. */
function asNumber(value: RuleValue | null | undefined): number | undefined {
  return typeof value === 'number' ? value : undefined;
}

/**
 * A template publication, with its version number and the note describing what changed.
 *
 * The note is the only account of *why* the text a patient signs changed on a given day, which is why the
 * backend requires it — so it is shown rather than hidden behind a click into the template.
 */
function withVersionNote(prefix: string, payload: Payload): string {
  const versioned = payload.versionNumber ? `${prefix} (versão ${payload.versionNumber})` : prefix;
  return payload.note ? `${versioned} — ${payload.note}` : versioned;
}
